import { Box, Button, Card, Collapse, Form, FormGroup, Link, Select } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { FundingErrorCode } from '@common/enums';
import type { NotFundedReason } from '@common/enums/notFundedReason';
import type { IOption } from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils';
import { ServiceType, TreatedStatusFilter } from '@common/enums';
import { CarePathType } from '@common/enums/carePathType';
import { financingSearchInputFilterValidationSchema } from '@common/validation/financing';
import { getSelectedLanguage } from '@frontend/translate';
import { emptyValidationErrors, updateValidationErrorsForMany, useAbortableFetch, validate } from '@frontend/utils';
import { formatDateIntoString } from '@frontend/utils/formatDateIntoString';
import { getSelectArrayValueObject } from '@frontend/utils/getSelectObject';
import { handleValidatePeriod } from '@frontend/utils/validateDatePeriod';
import type { BatchRun, FinancingSearchInputFilters } from '../financingSearchInputFilters';
import { BatchRunFilter } from '../../financingSearchFilters/BatchRunFilter';
import { ExportStatusFilters } from '../../financingSearchFilters/ExportStatusFilters';
import { PeriodFilter } from '../../financingSearchFilters/PeriodFilter';
import { SearchForFilter } from '../../financingSearchFilters/SearchForFilter';
import { ServiceFilter } from '../../financingSearchFilters/ServiceFilter';
import { FinancingTypeFilter } from './FinancingTypeFilter';
import { IdentifierSocialSecurityFilter } from './IdentifierSocialSecurityFilter';
import { RequestStatusFilter } from './RequestStatusFilter';

type FinancingSearchFiltersProps = {
    disableSearch: boolean;
    financingSearchFilters: FinancingSearchInputFilters;
    fundingErrorInformations: IOption<string>[];
    fundingErrors: IOption<FundingErrorCode>[];
    notFundedReasonValues: IOption<NotFundedReason>[];
    onSearch: () => Promise<void>;
    onSetFinancingSearchFilters: (filters: FinancingSearchInputFilters) => void;
};

export function FinancingSearchFilters({
    onSetFinancingSearchFilters,
    financingSearchFilters,
    notFundedReasonValues,
    fundingErrors,
    fundingErrorInformations,
    disableSearch,
    onSearch,
}: FinancingSearchFiltersProps) {
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();

    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [showAllFilters, setShowAllFilters] = useState(false);
    const [insurerFilterValues, setInsurerFilterValues] = useState<IOption<string>[]>([]);
    const [isLoadingInsurerFilters, setIsLoadingInsurerFilters] = useState(true);

    useEffect(() => {
        loadInsurerFilters();
    }, []);

    useEffect(() => {
        validateSearchFilters();
    }, [financingSearchFilters]);

    return (
        <Card>
            <Card.Body>
                <Form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        await onSearch();
                    }}
                    role="search"
                    submitOnEnter={true}
                >
                    <Box maxWidth={'900px'}>
                        <FormGroup label={translate.common.processesBetween}>
                            <PeriodFilter
                                endDate={financingSearchFilters.endDate}
                                errors={errors}
                                onChangeDate={(name, value) => handleSetFilter({ [name]: value })}
                                onValidate={(name, value) =>
                                    handleValidatePeriod({
                                        datePeriod: {
                                            endDate: name === 'endDate' ? value : financingSearchFilters.endDate,
                                            startDate: name === 'startDate' ? value : financingSearchFilters.startDate,
                                        },
                                        errors,
                                        schema: financingSearchInputFilterValidationSchema,
                                        setErrors,
                                    })
                                }
                                startDate={financingSearchFilters.startDate}
                            />
                        </FormGroup>
                        <SearchForFilter
                            errors={errors}
                            onChangeSearchField={(value) => handleSetFilter({ searchField: value })}
                            onChangeSearchText={(value) => handleSetFilter({ searchText: value })}
                            searchField={financingSearchFilters.searchField}
                            searchText={financingSearchFilters.searchText}
                        />
                        <ServiceFilter
                            carePathType={CarePathType.old}
                            onChange={({ episodeCategoryId, serviceId }) =>
                                handleSetFilter({ episodeCategoryId, serviceId })
                            }
                            selectedValue={financingSearchFilters.episodeCategoryId || financingSearchFilters.serviceId}
                        />
                        <IdentifierSocialSecurityFilter
                            errors={errors}
                            identifierSocialSecurity={financingSearchFilters.identifierSocialSecurity}
                            onChangeIdentifierSocialSecurity={(value) =>
                                handleSetFilter({ identifierSocialSecurity: value })
                            }
                        />
                        <Collapse isOpen={showAllFilters}>
                            <FormGroup label={translate.common.processType}>
                                <Select
                                    isClearable
                                    onChange={(item) =>
                                        handleSetFilter({
                                            serviceType: item?.value,
                                        })
                                    }
                                    options={Object.values(ServiceType).map((serviceType) => {
                                        return {
                                            label: translate.serviceTypeEnum[serviceType],
                                            value: serviceType,
                                        };
                                    })}
                                    value={
                                        financingSearchFilters.serviceType && {
                                            label: translate.serviceTypeEnum[financingSearchFilters.serviceType],
                                            value: financingSearchFilters.serviceType,
                                        }
                                    }
                                />
                            </FormGroup>
                            <BatchRunFilter
                                batchRun={financingSearchFilters.batchRun}
                                errors={errors}
                                onChange={handleBatchRunFilterChange}
                            />
                            <FormGroup label={translate.request.reasonNoInvoiceOrDeclaration}>
                                <Select
                                    isClearable
                                    onChange={(item) =>
                                        handleSetFilter({
                                            notFundedReason: item?.value,
                                        })
                                    }
                                    options={notFundedReasonValues}
                                    value={getSelectArrayValueObject(
                                        financingSearchFilters.notFundedReason,
                                        notFundedReasonValues
                                    )}
                                />
                            </FormGroup>
                            <FormGroup label={translate.request.fundingError}>
                                <Select
                                    isClearable
                                    onChange={(item) =>
                                        handleSetFilter({
                                            fundingError: item?.value,
                                        })
                                    }
                                    options={fundingErrors}
                                    value={getSelectArrayValueObject(
                                        financingSearchFilters.fundingError,
                                        fundingErrors
                                    )}
                                />
                            </FormGroup>
                            <RequestStatusFilter
                                onChangeTreatedStatus={(value) => handleSetFilter({ treatedStatus: value })}
                                treatedStatus={financingSearchFilters.treatedStatus}
                            />
                            <FormGroup label={translate.request.fundingErrorInformation}>
                                <Select
                                    isClearable
                                    isDisabled={financingSearchFilters.treatedStatus !== TreatedStatusFilter.treated}
                                    onChange={(item) =>
                                        handleSetFilter({
                                            fundingErrorInformation: item?.value,
                                        })
                                    }
                                    options={fundingErrorInformations}
                                    value={getSelectArrayValueObject(
                                        financingSearchFilters.fundingErrorInformation,
                                        fundingErrorInformations
                                    )}
                                />
                            </FormGroup>
                            <FormGroup label={translate.insurers.insurer}>
                                <Select
                                    isClearable
                                    isLoading={isLoadingInsurerFilters}
                                    loadingMessage={() => translate.common.loading}
                                    onChange={(item) => handleSetFilter({ insurerId: item?.value })}
                                    options={insurerFilterValues}
                                    value={getSelectArrayValueObject(
                                        financingSearchFilters.insurerId,
                                        insurerFilterValues
                                    )}
                                />
                            </FormGroup>
                            <FinancingTypeFilter
                                noInvoiceNorDeclaration={financingSearchFilters.noInvoiceNorDeclaration}
                                onChangeNoInvoiceNorDeclaration={(value) =>
                                    handleSetFilter({ noInvoiceNorDeclaration: value })
                                }
                                onChangeOnlyDeclarations={(value) => handleSetFilter({ onlyDeclarations: value })}
                                onChangeOnlyInvoices={(value) => handleSetFilter({ onlyInvoices: value })}
                                onlyDeclarations={financingSearchFilters.onlyDeclarations}
                                onlyInvoices={financingSearchFilters.onlyInvoices}
                            />
                            <ExportStatusFilters
                                exportStatus={financingSearchFilters.exportStatus}
                                onChangeExportStatus={(value) =>
                                    handleSetFilter({
                                        exportStatus: value,
                                    })
                                }
                            />
                        </Collapse>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                        >
                            <Button
                                isDisabled={disableSearch || !errors.isEmpty()}
                                type="submit"
                            >
                                {translate.common.searchRefresh}
                            </Button>
                            <Link
                                color="subtle"
                                fontWeight="normal"
                                iconLeft={showAllFilters ? 'caret-up' : 'caret-down'}
                                my={2}
                                onClick={() => setShowAllFilters(!showAllFilters)}
                                size="xs"
                                variant="button"
                            >
                                {showAllFilters ? translate.finance.lessFilters : translate.finance.moreFilters}
                            </Link>
                        </Box>
                    </Box>
                </Form>
            </Card.Body>
        </Card>
    );

    function handleSetFilter(filter: Partial<FinancingSearchInputFilters>) {
        onSetFinancingSearchFilters({ ...financingSearchFilters, ...filter });
    }

    function handleBatchRunFilterChange(batchRun?: BatchRun): void {
        const batchRunFilter = { batchRun };

        handleSetFilter(batchRunFilter);

        const newErrors = updateValidationErrorsForMany(
            financingSearchInputFilterValidationSchema,
            batchRunFilter,
            errors,
            ['batchRun.batchType', 'batchRun.runNumber']
        );

        setErrors(newErrors);
    }

    async function loadInsurerFilters() {
        const result = await abortableFetch.fetchJson('/api/insurers?limit=100', 'GET');
        const insurerOptions = result.json.items.map((item: { id: string; name: string }) => {
            return { label: item.name, value: item.id };
        });
        setInsurerFilterValues(insurerOptions);
        setIsLoadingInsurerFilters(false);
    }

    function validateSearchFilters() {
        const { errors: newErrors } = validate(financingSearchInputFilterValidationSchema, {
            ...financingSearchFilters,
            endDate: formatDateIntoString(financingSearchFilters.endDate),
            startDate: formatDateIntoString(financingSearchFilters.startDate),
        });

        setErrors(newErrors);
    }
}
